import { env } from '@/config/env';
import type { Locale } from '@/i18n/translations';

import { DonorError } from '@/api/errors';
import { requestJson } from '@/api/transport';
import { parseCriticalResponse } from './response-schemas';
export { DonorError } from '@/api/errors';

export interface RequestOptions {
  method?: string;
  body?: unknown;
  token?: string;
  guest?: boolean;
  proof?: string;
  key?: string;
  signal?: AbortSignal | undefined;
}
export type ProtectedRequestOptions = Omit<RequestOptions, 'guest' | 'token'>;
export async function send<T>(
  path: string,
  locale: Locale,
  options: RequestOptions = {},
): Promise<T> {
  const value = await requestJson(`${env.apiBaseUrl}${path}`, {
    method: options.method ?? 'GET',
    credentials:
      options.guest || options.token || path.startsWith('/auth/web/') ? 'include' : 'omit',
    cache: 'no-store',
    ...(options.signal ? { signal: options.signal } : {}),
    headers: {
      'X-App-Language': locale,
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(options.guest || options.token ? { 'X-Giving-Request': '1' } : {}),
      ...(path.startsWith('/auth/web/') ? { 'X-Web-Request': '1' } : {}),
      ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      ...(options.proof ? { 'X-Browser-Verification': options.proof } : {}),
      ...(options.key ? { 'Idempotency-Key': options.key } : {}),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });
  return parseCriticalResponse(path, value) as T;
}

export function guestRequest<T>(
  path: string,
  locale: Locale,
  options: ProtectedRequestOptions = {},
): Promise<T> {
  return send(path, locale, { ...options, guest: true });
}

export function readStored<T>(key: string, fallback: T): T {
  try {
    return (JSON.parse(localStorage.getItem(key) ?? 'null') as T) ?? fallback;
  } catch {
    return fallback;
  }
}
/** Recovery and auth metadata must never silently become an empty/default operation. */
export function readStoredStrict(key: string, fallback: unknown): unknown {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as unknown;
  } catch {
    throw new DonorError(0, 'STORAGE_REQUIRED');
  }
}
export function storeRequired(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    throw new DonorError(0, 'STORAGE_REQUIRED');
  }
}
export function browserCapabilities(): void {
  if (!navigator.locks || typeof BroadcastChannel === 'undefined')
    throw new DonorError(0, 'BROWSER_UNSUPPORTED');
  try {
    const probe = new BroadcastChannel('giving:capability-probe');
    probe.close?.();
  } catch {
    throw new DonorError(0, 'BROWSER_UNSUPPORTED');
  }
  const key = 'giving.storage-probe';
  storeRequired(key, true);
  localStorage.removeItem(key);
}
export async function locked<T>(name: string, work: () => T | Promise<T>): Promise<T> {
  browserCapabilities();
  return navigator.locks.request(`giving:${name}`, work);
}
